import React, {
	useState,
	useRef,
	useEffect,
	useCallback,
	useMemo,
} from "react";
import { FormGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { employeesService } from "services";
import "./EmployeeSelectSearch.scss";

const DEFAULT_PAGE_SIZE = 40;
const SEARCH_DEBOUNCE_MS = 350;
/** Mínimo de caracteres para enviar `q` al API y no saturar el backend */
const MIN_SEARCH_CHARS = 2;

function normId(id) {
	if (id === null || id === undefined) return null;
	const s = String(id).trim();
	if (s === "" || s === "-1") return null;
	return s;
}

function parseMultipleValue(value) {
	if (value === null || value === undefined) return [];
	if (Array.isArray(value)) {
		return value.map((v) => normId(v)).filter(Boolean);
	}
	if (typeof value === "string") {
		return value
			.split(",")
			.map((id) => id.replace(/[\[\]]/g, "").trim())
			.map(normId)
			.filter(Boolean);
	}
	return [];
}

function mergeById(existing, incoming) {
	const map = new Map();
	(existing || []).forEach((e) => map.set(String(e.id), e));
	(incoming || []).forEach((e) => map.set(String(e.id), e));
	return Array.from(map.values());
}

/**
 * Selector / buscador de empleados contra GET /employees (variant=select),
 * con página vía scroll y búsqueda remota (`q` solo con 2+ caracteres en el buscador).
 *
 * @param {boolean} [multiple=false] — selección múltiple
 * @param {string|number|null|Array} value — id o lista de ids (en múltiple admite string CSV)
 * @param {function} onChange — single: `(id: string|null, employee?: object)` al elegir fila; multiple: `(ids: string[])`; si `allowClear` y “Todos”, single recibe `(null, null)`.
 * @param {boolean} [allowClear=true] — en modo single, muestra “Todos” y permite `onChange(null)`; si es false, siempre hay un empleado elegido.
 * @param {object} [fetchOptions] — opciones extra para getSelectable (enabled, group_id, active)
 * @param {string} [className] — clases extra en el FormGroup; usar `form-select-user` para ancho máximo 250px (ver SCSS del componente)
 * @param {object} [knownEmployee] — modo single: `{ id, name, surname, code? }` del valor actual si el padre ya lo conoce (p. ej. usuario logueado); evita el texto “Empleado seleccionado” cuando aún no está en la página cargada del desplegable.
 * @param {object|null} [pinnedShortcut] — modo single: `{ id, label, employee }`. Si el valor elegido no es ese `id`, se muestra una fila fija arriba (ej. “Mis ausencias”) para volver a ese empleado sin buscar en la lista.
 */
const EmployeeSelectSearch = ({
	multiple = false,
	value,
	onChange,
	fetchOptions,
	pageSize = DEFAULT_PAGE_SIZE,
	placeholder = "Buscar empleado",
	allLabel = "Todos los empleados",
	allowClear = true,
	knownEmployee,
	pinnedShortcut,
	className = "",
	disabled = false,
	controlId,
	excludeIds = [],
}) => {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [searchInput, setSearchInput] = useState("");
	const [debouncedQ, setDebouncedQ] = useState("");
	const [rows, setRows] = useState([]);
	const [hasMore, setHasMore] = useState(true);
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState(null);
	const [pickedExtras, setPickedExtras] = useState([]);

	const dropdownRef = useRef(null);
	const searchInputRef = useRef(null);
	const abortRef = useRef(null);
	const requestSeq = useRef(0);
	const rowsRef = useRef([]);
	const pageRef = useRef(0);

	const fetchSig = `${fetchOptions?.enabled ?? ""}|${fetchOptions?.group_id ?? ""}|${
		fetchOptions?.active ?? ""
	}`;

	const selectedIds = useMemo(() => {
		if (multiple) return parseMultipleValue(value);
		const n = normId(value);
		return n ? [n] : [];
	}, [multiple, value]);

	useEffect(() => {
		rowsRef.current = rows;
	}, [rows]);

	useEffect(() => {
		setPickedExtras((prev) =>
			prev.filter((e) => selectedIds.includes(String(e.id)))
		);
	}, [selectedIds]);

	useEffect(() => {
		const t = setTimeout(
			() => setDebouncedQ(searchInput.trim()),
			SEARCH_DEBOUNCE_MS
		);
		return () => clearTimeout(t);
	}, [searchInput]);

	const resetAndLoad = useCallback(async () => {
		const qTrim = debouncedQ.trim();

		if (qTrim.length === 1) {
			if (abortRef.current) abortRef.current.abort();
			requestSeq.current += 1;
			setLoading(false);
			setLoadingMore(false);
			setError(null);
			pageRef.current = 0;
			rowsRef.current = [];
			setRows([]);
			setHasMore(false);
			return;
		}

		if (abortRef.current) abortRef.current.abort();
		const ac = new AbortController();
		abortRef.current = ac;
		const seq = ++requestSeq.current;

		setLoading(true);
		setError(null);
		pageRef.current = 0;
		setRows([]);
		setHasMore(true);

		try {
			const opts = {
				...(fetchOptions || {}),
				page: 0,
				page_size: pageSize,
			};
			if (qTrim.length >= MIN_SEARCH_CHARS) opts.q = qTrim;

			const res = await employeesService.getSelectable(opts, ac.signal);
			if (seq !== requestSeq.current) return;

			if (!res.ok) {
				setError(t('employeeSelect.errorLoading'));
				setRows([]);
				setHasMore(false);
				return;
			}

			const chunk = Array.isArray(res.data) ? res.data : [];
			rowsRef.current = chunk;
			setRows(chunk);

			const total = res.pagination?.total_items;
			const computedHasMore =
				typeof total === "number" && total >= 0
					? chunk.length < total
					: chunk.length === pageSize;
			setHasMore(computedHasMore);

			if (!multiple && !computedHasMore && !normId(value)) {
				const visibleChunk =
					excludeIds.length > 0
						? chunk.filter((e) => !excludeIds.includes(String(e.id)))
						: chunk;
				if (visibleChunk.length === 1) {
					const emp = visibleChunk[0];
					setPickedExtras((prev) => mergeById(prev, [emp]));
					onChange(String(emp.id), emp);
				}
			}
		} catch (e) {
			if (e.name === "AbortError") return;
			setError(t('employeeSelect.errorLoading'));
			setRows([]);
			setHasMore(false);
		} finally {
			if (seq === requestSeq.current) setLoading(false);
		}
	}, [debouncedQ, fetchSig, pageSize]);

	const loadMore = useCallback(async () => {
		if (!hasMore || loading || loadingMore) return;
		const qTrim = debouncedQ.trim();
		if (qTrim.length === 1) return;

		if (abortRef.current) abortRef.current.abort();
		const ac = new AbortController();
		abortRef.current = ac;
		const seq = ++requestSeq.current;
		const nextPage = pageRef.current + 1;

		setLoadingMore(true);
		setError(null);

		try {
			const opts = {
				...(fetchOptions || {}),
				page: nextPage,
				page_size: pageSize,
			};
			if (qTrim.length >= MIN_SEARCH_CHARS) opts.q = qTrim;

			const res = await employeesService.getSelectable(opts, ac.signal);
			if (seq !== requestSeq.current) return;

			if (!res.ok) {
				setHasMore(false);
				return;
			}

			const chunk = Array.isArray(res.data) ? res.data : [];
			const merged = mergeById(rowsRef.current, chunk);
			rowsRef.current = merged;
			setRows(merged);
			pageRef.current = nextPage;

			const total = res.pagination?.total_items;
			if (typeof total === "number" && total >= 0) {
				setHasMore(merged.length < total);
			} else {
				setHasMore(chunk.length === pageSize);
			}
		} catch (e) {
			if (e.name === "AbortError") return;
			setHasMore(false);
		} finally {
			if (seq === requestSeq.current) setLoadingMore(false);
		}
	}, [debouncedQ, fetchSig, pageSize, hasMore, loading, loadingMore]);

	useEffect(() => {
		if (!isOpen) return;
		resetAndLoad();
	}, [isOpen, debouncedQ, resetAndLoad]);

	useEffect(() => {
		if (!isOpen) return;
		if (searchInputRef.current) {
			searchInputRef.current.focus();
		}
	}, [isOpen]);

	useEffect(() => {
		const onDoc = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
				setSearchInput("");
			}
		};
		document.addEventListener("mousedown", onDoc);
		return () => document.removeEventListener("mousedown", onDoc);
	}, []);

	const selectedObjects = useMemo(() => {
		const byId = new Map();
		mergeById(rows, pickedExtras).forEach((e) => byId.set(String(e.id), e));
		if (
			!multiple &&
			knownEmployee &&
			knownEmployee.id != null &&
			selectedIds.includes(String(knownEmployee.id)) &&
			!byId.has(String(knownEmployee.id))
		) {
			byId.set(String(knownEmployee.id), knownEmployee);
		}
		return selectedIds.map((id) => byId.get(id)).filter(Boolean);
	}, [rows, pickedExtras, selectedIds, multiple, knownEmployee]);

	const triggerLabel = useMemo(() => {
		if (multiple) {
			if (selectedIds.length === 0) {
				return <span className="placeholder">{allLabel}</span>;
			}
			if (selectedObjects.length === 1) {
				const e = selectedObjects[0];
				return (
					<span className="selected-item">
						{e.name} {e.surname}
						{e.code ? ` (${e.code})` : ""}
					</span>
				);
			}
			return (
				<span className="selected-count">
					{t('employeeSelect.selectedCount', { count: selectedIds.length })}
				</span>
			);
		}
		if (selectedIds.length === 0) {
			return <span className="placeholder">{placeholder}</span>;
		}
		if (selectedObjects.length === 1) {
			const e = selectedObjects[0];
			return (
				<span className="selected-item">
					{e.name} {e.surname}
					{e.code ? ` (${e.code})` : ""}
				</span>
			);
		}
		return <span className="selected-item">{t('employeeSelect.selectedOne')}</span>;
	}, [multiple, selectedIds, selectedObjects, allLabel, placeholder]);

	const rememberPicked = useCallback((emp) => {
		setPickedExtras((prev) => mergeById(prev, [emp]));
	}, []);

	const handleToggleOpen = () => {
		if (disabled) return;
		setIsOpen((o) => !o);
		if (isOpen) setSearchInput("");
	};

	const handlePickAll = () => {
		if (multiple || !allowClear) return;
		onChange(null, null);
		setIsOpen(false);
		setSearchInput("");
	};

	const showPinnedShortcut =
		!multiple &&
		pinnedShortcut &&
		pinnedShortcut.id != null &&
		pinnedShortcut.employee &&
		normId(value) !== String(pinnedShortcut.id);

	const handlePinnedShortcutClick = () => {
		if (!pinnedShortcut?.employee) return;
		rememberPicked(pinnedShortcut.employee);
		onChange(String(pinnedShortcut.id), pinnedShortcut.employee);
		setIsOpen(false);
		setSearchInput("");
	};

	const handleToggleEmployee = (emp) => {
		const idStr = String(emp.id);
		if (multiple) {
			const set = new Set(selectedIds);
			if (set.has(idStr)) set.delete(idStr);
			else set.add(idStr);
			rememberPicked(emp);
			onChange(Array.from(set));
			return;
		}
		rememberPicked(emp);
		onChange(idStr, emp);
		setIsOpen(false);
		setSearchInput("");
	};

	const handleClearMultiple = () => {
		onChange([]);
	};

	const onListScroll = (e) => {
		const el = e.target;
		if (!hasMore || loading || loadingMore) return;
		if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
			loadMore();
		}
	};

	const isSelected = (id) => selectedIds.includes(String(id));

	return (
		<FormGroup
			className={`employee-select-search ${className}`}
			style={{ position: "relative" }}
			controlId={controlId}
		>
			<div className="custom-select-container" ref={dropdownRef}>
				<div
					className={`custom-select select-control ${isOpen ? "open" : ""} ${
						disabled ? "disabled" : ""
					}`}
					onClick={handleToggleOpen}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							handleToggleOpen();
						}
					}}
					role="button"
					tabIndex={disabled ? -1 : 0}
				>
					<div className="selected-items">{triggerLabel}</div>
				</div>

				{isOpen && (
					<div className="dropdown-menu">
						<div className="dropdown-header">
							{multiple && selectedIds.length > 0 && (
								<button
									type="button"
									className="clear-all-btn"
									onClick={handleClearMultiple}
								>
									{t('employeeSelect.clearSelection')}
								</button>
							)}
							<input
								ref={searchInputRef}
								type="text"
								placeholder={t('employeeSelect.searchPlaceholder')}
								value={searchInput}
								onChange={(e) => setSearchInput(e.target.value)}
								className="search-input"
								onClick={(e) => e.stopPropagation()}
							/>
						</div>

						<div className="dropdown-options" onScroll={onListScroll}>
							{!multiple && allowClear && (
								<div
									className={`dropdown-option muted ${
										selectedIds.length === 0 ? "selected" : ""
									}`}
									onClick={handlePickAll}
								>
									<span className="employee-name">{allLabel}</span>
								</div>
							)}

							{showPinnedShortcut && (
								<div
									className="dropdown-option pinned-shortcut"
									onClick={handlePinnedShortcutClick}
								>
									<div className="option-content">
										<span className="employee-name">
											{pinnedShortcut.label}
										</span>
										<span className="employee-code">
											{`${pinnedShortcut.employee.name || ""} ${
												pinnedShortcut.employee.surname || ""
											}`.trim()}
										</span>
									</div>
								</div>
							)}

							{rows.filter((e) => !excludeIds.includes(String(e.id))).map((employee) => {
								const sel = isSelected(employee.id);
								return (
									<div
										key={employee.id}
										className={`dropdown-option ${sel ? "selected" : ""}`}
										onClick={() => handleToggleEmployee(employee)}
									>
										<div className="option-content">
											<span className="employee-name">
												{employee.name} {employee.surname}
											</span>
											{employee.code ? (
												<span className="employee-code">({employee.code})</span>
											) : null}
										</div>
										{multiple && sel ? (
											<div className="checkmark">✓</div>
										) : null}
									</div>
								);
							})}

							{!loading && rows.length === 0 && (
								<div className="no-results">
									{debouncedQ.trim().length === 1
										? t('employeeSelect.minCharsHint', { min: MIN_SEARCH_CHARS })
										: t('employeeSelect.noResults')}
								</div>
							)}

							{(loading || loadingMore) && (
								<div className="list-footer">{t('common.loading')}</div>
							)}
						</div>
					</div>
				)}
			</div>

			{error && <small className="text-danger">{error}</small>}
		</FormGroup>
	);
};

export default EmployeeSelectSearch;
